// Merchant web app router. Every page is lazy-loaded so the initial
// bundle stays under the budget while the IA surface keeps growing in
// M1. The <AppLayout> wraps the authenticated surface; the public
// pages (login / register / forgot-password / invite) live outside
// the layout so the chrome (header / sidebar) does not flash for
// unauthenticated visitors.

import { lazy, Suspense, type JSX } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  type RouteObject,
} from "react-router-dom";

import { AppLayout } from "@/shared/layout/AppLayout";
import { NotFoundPage } from "@/pages/NotFoundPage";

const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const CreateSinglePage = lazy(() =>
  import("@/pages/CreateSinglePage").then((m) => ({ default: m.CreateSinglePage })),
);
const CreateWorkflowPage = lazy(() =>
  import("@/pages/CreateWorkflowPage").then((m) => ({ default: m.CreateWorkflowPage })),
);
const DraftsPage = lazy(() =>
  import("@/pages/DraftsPage").then((m) => ({ default: m.DraftsPage })),
);
const TasksPage = lazy(() =>
  import("@/pages/TasksPage").then((m) => ({ default: m.TasksPage })),
);
const ProductsPage = lazy(() =>
  import("@/pages/ProductsPage").then((m) => ({ default: m.ProductsPage })),
);
const AssetsPage = lazy(() =>
  import("@/pages/AssetsPage").then((m) => ({ default: m.AssetsPage })),
);
const AvatarsPage = lazy(() =>
  import("@/pages/AvatarsPage").then((m) => ({ default: m.AvatarsPage })),
);
const HistoryPage = lazy(() =>
  import("@/pages/HistoryPage").then((m) => ({ default: m.HistoryPage })),
);
const PublishCalendarPage = lazy(() =>
  import("@/pages/PublishCalendarPage").then((m) => ({ default: m.PublishCalendarPage })),
);
const PlatformAccountsPage = lazy(() =>
  import("@/pages/PlatformAccountsPage").then((m) => ({ default: m.PlatformAccountsPage })),
);
const PublishReviewPage = lazy(() =>
  import("@/pages/PublishReviewPage").then((m) => ({ default: m.PublishReviewPage })),
);
const PublishHistoryPage = lazy(() =>
  import("@/pages/PublishHistoryPage").then((m) => ({ default: m.PublishHistoryPage })),
);
const AnalyticsVideosPage = lazy(() =>
  import("@/pages/AnalyticsVideosPage").then((m) => ({ default: m.AnalyticsVideosPage })),
);
const AnalyticsProductsPage = lazy(() =>
  import("@/pages/AnalyticsProductsPage").then((m) => ({ default: m.AnalyticsProductsPage })),
);
const SettingsTeamPage = lazy(() =>
  import("@/pages/SettingsTeamPage").then((m) => ({ default: m.SettingsTeamPage })),
);
const SettingsRolesPage = lazy(() =>
  import("@/pages/SettingsRolesPage").then((m) => ({ default: m.SettingsRolesPage })),
);
const SettingsAccountPage = lazy(() =>
  import("@/pages/SettingsAccountPage").then((m) => ({ default: m.SettingsAccountPage })),
);
const SettingsQuotaPage = lazy(() =>
  import("@/pages/SettingsQuotaPage").then((m) => ({ default: m.SettingsQuotaPage })),
);
const SettingsExtensionPage = lazy(() =>
  import("@/pages/SettingsExtensionPage").then((m) => ({ default: m.SettingsExtensionPage })),
);
const HelpPage = lazy(() =>
  import("@/pages/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const InvitePage = lazy(() =>
  import("@/pages/InvitePage").then((m) => ({ default: m.InvitePage })),
);
const ExtensionDownloadPage = lazy(() =>
  import("@/pages/ExtensionDownloadPage").then((m) => ({ default: m.ExtensionDownloadPage })),
);

const APP_NAME = "Video Marketing Assistant";

const AuthenticatedLayout = (): JSX.Element => (
  <AppLayout appName={APP_NAME}>
    <Outlet />
  </AppLayout>
);

const authenticatedRoutes: RouteObject[] = [
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/create/single", element: <CreateSinglePage /> },
  { path: "/create/workflow", element: <CreateWorkflowPage /> },
  { path: "/create/drafts", element: <DraftsPage /> },
  { path: "/create/tasks", element: <TasksPage /> },
  { path: "/assets/products", element: <ProductsPage /> },
  { path: "/assets/viral", element: <AssetsPage /> },
  { path: "/assets/avatars", element: <AvatarsPage /> },
  { path: "/assets/history", element: <HistoryPage /> },
  { path: "/publish/calendar", element: <PublishCalendarPage /> },
  { path: "/publish/accounts", element: <PlatformAccountsPage /> },
  { path: "/publish/review", element: <PublishReviewPage /> },
  { path: "/publish/history", element: <PublishHistoryPage /> },
  { path: "/analytics/videos", element: <AnalyticsVideosPage /> },
  { path: "/analytics/products", element: <AnalyticsProductsPage /> },
  { path: "/settings/team", element: <SettingsTeamPage /> },
  { path: "/settings/roles", element: <SettingsRolesPage /> },
  { path: "/settings/account", element: <SettingsAccountPage /> },
  { path: "/settings/quota", element: <SettingsQuotaPage /> },
  { path: "/settings/extension", element: <SettingsExtensionPage /> },
  { path: "/help", element: <HelpPage /> },
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      ...authenticatedRoutes,
    ],
  },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  { path: "/extension/download", element: <ExtensionDownloadPage /> },
  { path: "*", element: <NotFoundPage /> },
]);

function PageFallback(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-64 items-center justify-center text-sm text-fg-muted"
    >
      加载中…
    </div>
  );
}

export function AppRouter(): JSX.Element {
  return (
    <Suspense fallback={<PageFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
